import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useAuthStore } from "@/stores/authStore";
import { useRefData } from "@/hooks/useRefData";
import { Sidebar } from "@/components/layout/Sidebar";
import { IncomingCallPopup } from "@/components/shared/IncomingCallPopup";
import { Spinner } from "@/components/shared/Badges";
import Login from "@/pages/Login";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Call = lazy(() => import("@/pages/Call"));
const Contacts = lazy(() => import("@/pages/Contacts"));
const ContactDetail = lazy(() => import("@/pages/Contacts/ContactDetail"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const QuickTasks = lazy(() => import("@/pages/Tasks/QuickTasks"));
const Pipeline = lazy(() => import("@/pages/Pipeline"));
const Admin = lazy(() => import("@/pages/Admin"));
const Webinars = lazy(() => import("@/pages/Webinars"));
const Deals = lazy(() => import("@/pages/Deals"));
const Inbox = lazy(() => import("@/pages/Inbox"));
const Calls = lazy(() => import("@/pages/Calls"));
const Callbacks = lazy(() => import("@/pages/Quality").then((m) => ({ default: m.Callbacks })));
const DataQuality = lazy(() => import("@/pages/Quality/DataQuality"));
const Review = lazy(() => import("@/pages/Review"));

const Shell = () => {
  useRefData();
  const isAdmin = useAuthStore((s) => s.user?.role === "admin");
  return (
    <div className="flex h-screen overflow-hidden bg-white text-gray-900">
      <Sidebar />
      <IncomingCallPopup />
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Suspense fallback={<div className="flex flex-1 items-center justify-center"><Spinner /></div>}>
          <Routes>
            <Route path="/" element={<Navigate to="/contacts" replace />} />
            <Route path="/call" element={<Call />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/contacts/:id" element={<ContactDetail />} />
            <Route path="/tasks" element={<Tasks />} />
            <Route path="/tasks/quick" element={<QuickTasks />} />
            <Route path="/pipeline" element={<Pipeline />} />
            <Route path="/webinars" element={<Webinars />} />
            <Route path="/deals" element={<Deals />} />
            <Route path="/inbox" element={<Inbox />} />
            <Route path="/calls" element={<Calls />} />
            <Route path="/callbacks" element={<Callbacks />} />
            <Route path="/quality" element={<DataQuality />} />
            <Route path="/review" element={<Review />} />
            <Route path="/faulty" element={<Navigate to="/quality" replace />} />
            {isAdmin && <Route path="/admin/*" element={<Admin />} />}
            <Route path="*" element={<Navigate to="/contacts" replace />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
};

/** Ctrl/⌘+klik nebo prostřední tlačítko na prvku s data-href otevře odkaz v novém okně (tlačítka nejsou <a>, prohlížeč to sám neumí). */
const useOpenInNewTab = () => {
  useEffect(() => {
    const target = (e) => e.target?.closest?.("[data-href]");
    const onClick = (e) => {
      const el = target(e);
      if (!el || !(e.ctrlKey || e.metaKey || e.shiftKey)) return;
      e.preventDefault(); e.stopPropagation();
      window.open(el.dataset.href, "_blank", e.shiftKey ? "noopener" : undefined);
    };
    const onAux = (e) => {
      const el = target(e);
      if (!el || e.button !== 1) return;
      e.preventDefault(); e.stopPropagation();
      window.open(el.dataset.href, "_blank");
    };
    const onMouseDown = (e) => { if (e.button === 1 && target(e)) e.preventDefault(); };
    document.addEventListener("click", onClick, true);
    document.addEventListener("auxclick", onAux, true);
    document.addEventListener("mousedown", onMouseDown, true);
    return () => { document.removeEventListener("click", onClick, true); document.removeEventListener("auxclick", onAux, true); document.removeEventListener("mousedown", onMouseDown, true); };
  }, []);
};

export default function App() {
  const token = useAuthStore((s) => s.token);
  useOpenInNewTab();
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors closeButton toastOptions={{ className: "text-base" }} />
      {token ? <Shell /> : <Login />}
    </BrowserRouter>
  );
}
